<script>
  import { API_BASE_URL, isAdminUser } from '../../config.js';

  export let selectedProject = null;
  export let user = null;
  export let applications = [];
  export let applyLoading = false;
  export let applicationError = null;
  export let projectPrice = '';
  export let applyMessage = '';
  export let availableDate = '';
  export let startWorkDate = '';
  export let statusOptions = [];
  export let statusMapping = {};
  export let reverseStatusMapping = {};
  export let onApplyToProject = () => {};
  export let onFetchProjectApplications = () => {};
  export let onSetPage = () => {};
  export let onCloseProjectDetail = () => {};
  export let formatProjectPeriod = () => {};
  export let formatRecruitmentPeriod = () => {};
  export let validateNumber = () => {};

  let userFiles = [];
  let hasUserFiles = false;

  // 사용자 파일 존재 여부 확인
  async function checkUserFiles() {
    if (!user) {
      hasUserFiles = false;
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/files/my-files`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        userFiles = data.files || [];
        hasUserFiles = userFiles.length > 0;
      } else {
        hasUserFiles = false;
      }
    } catch (error) {
      console.error('사용자 파일 조회 오류:', error);
      hasUserFiles = false;
    }
  }

  // 사용자 정보가 변경될 때 파일 확인
  $: if (user) {
    checkUserFiles();
  }

  // 지원 불가능 여부 (이미 지원했거나 공고가 마감된 경우 또는 파일이 없는 경우)
  $: isApplyDisabled = selectedProject && (
    selectedProject.hasApplied || 
    selectedProject.status === '마감' || 
    selectedProject.deadline === '마감' ||
    !hasUserFiles
  );

  // 지원 불가 사유 메시지
  $: applyDisabledReason = (() => {
    if (!selectedProject) return '';
    if (selectedProject.hasApplied) return '이미 지원한 공고입니다.';
    if (selectedProject.status === '마감' || selectedProject.deadline === '마감') return '마감된 공고입니다.';
    if (!hasUserFiles) return '지원을 위해서는 먼저 파일을 업로드해야 합니다.\n마이페이지 > 정보수정 > 첨부 문서에서 파일을 업로드하세요.';
    return '';
  })();

  function getUserDisplayName(app) {
    return app.userName || app.user?.name || app.user_name || app.user?.email || app.email || '이름 없음';
  }

  function getApplicationDate(app) {
    const dateValue = app.appliedAt || app.applied_at || app.createdAt || app.created_at || app.date;
    return dateValue ? new Date(dateValue).toLocaleDateString() : '-';
  }
  
  function getAvailableDate(app) {
    const dateValue = app.available_date;
    return dateValue ? dateValue : '-';
  }
  
  function getStartWorkDate(app) {
    const dateValue = app.start_work_date;
    return dateValue ? dateValue : '-';
  }

  function getStatusDisplay(app) {
    if (typeof app.status === 'number') {
      const statusOption = statusOptions.find(option => option.id === app.status);
      return {
        label: statusOption?.label || '알 수 없음',
        class: reverseStatusMapping[app.status] || ''
      };
    } else if (typeof app.status === 'string' && statusMapping[app.status] !== undefined) {
      const statusOption = statusOptions.find(option => option.id === statusMapping[app.status]);
      return {
        label: statusOption?.label || '알 수 없음',
        class: app.status
      };
    } else {
      const statusMap = {
        'applied': '지원완료',
        'reviewing': '검토중',
        'closed': '마감',
        'canceled': '지원 취소'
      };
      return {
        label: statusMap[app.status] || app.status || '알 수 없음',
        class: app.status || 'applied'
      };
    }
  }

  function getProjectApplications() {
    return applications.filter(app => 
      app.projectId === selectedProject.id || 
      app.project_id === selectedProject.id ||
      (app.project && app.project.id === selectedProject.id)
    );
  }

  function getApplicationsByStatus(status) {
    return getProjectApplications().filter(app => {
      if (status === 'reviewing') {
        return app.status === 'reviewing' || app.status === 1;
      } else if (status === 'accepted') {
        return app.status === 'accepted' || app.status === 3;
      }
      return false;
    });
  }
</script>

{#if selectedProject}
<div class="project-detail-container">
  <div class="project-detail-header">
    <div class="detail-badge-container">
      {#if selectedProject.type === '외주'}
        <span class="detail-badge outsourcing">외주</span>
      {:else}
        <span class="detail-badge">{selectedProject.type}</span>
      {/if}
      <span class="detail-badge status {selectedProject.status}">{selectedProject.status}</span>
    </div>
    
    <h1 class="project-detail-title">{selectedProject.title}</h1>
    
    <div class="project-basic-info">
      <div class="basic-info-row">
        <span class="info-label">경력</span>
        <span class="info-value">{selectedProject.level || '무관'}</span>
      </div>
      <div class="basic-info-row">
        <span class="info-label">회사</span>
        <span class="info-value">{selectedProject.companyName}</span>
      </div>
    </div>
    
    <div class="project-info-section">
      <div class="info-grid">
        <div class="info-item">
          <div class="info-label">공고</div>
          <div class="info-value">{selectedProject.type}</div>
        </div>
        <div class="info-item">
          <div class="info-label">예상 기간</div>
          <div class="info-value">{formatProjectPeriod(selectedProject.startDate, selectedProject.endDate) || '미정'}</div>
        </div>
        <div class="info-item">
          <div class="info-label">근무지</div>
          <div class="info-value">{selectedProject.location || '서울 구 서울아산병원'}</div>
        </div>
        <div class="info-item">
          <div class="info-label">채용 마감일</div>
          <div class="info-value">{formatRecruitmentPeriod(selectedProject.createdAt, selectedProject.deadlineDate) || '상시채용'}</div>
        </div>
      </div>
    </div>
    
    <div class="section-divider"></div>
    
    <div class="detail-updated">등록일: {new Date(selectedProject.createdAt).toLocaleDateString() || '2025-06-23'}</div>
  </div>
  
  <div class="project-detail-content">
    <h2 class="content-section-title">프로젝트 내용</h2>
    
    <div class="project-summary-box">
      <div class="content-list">
        <div class="content-item">
          <span class="content-number">1.</span>
          <span class="content-label">프로젝트명 :</span>
          <span class="content-value">{selectedProject.title}</span>
        </div>
        <div class="content-item">
          <span class="content-number">2.</span>
          <span class="content-label">담당업무 및 업무환경</span>
        </div>
        
        <div class="content-description">
          {#if selectedProject.content && selectedProject.content.length > 0}
            {#each selectedProject.content.slice(2) as line}
              <p>{line}</p>
            {/each}
          {:else}
            <p>{selectedProject.description || '프로젝트 설명이 없습니다.'}</p>
          {/if}
        </div>
      </div>
      
      <div class="summary-grid">
        <div class="summary-item">
          <div class="summary-label">기간</div>
          <div class="summary-value">{formatProjectPeriod(selectedProject.startDate, selectedProject.endDate) || '미정'}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">장소</div>
          <div class="summary-value">{selectedProject.location || '미정'}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">등급</div>
          <div class="summary-value">{selectedProject.level || '무관'}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">업무</div>
          <div class="summary-value">{selectedProject.type || '미정'}</div>
        </div>
      </div>
      
      {#if selectedProject.skills && selectedProject.skills.length > 0}
        <div class="summary-skills">
          <div class="summary-label">기술 스택</div>
          <div class="skills-badge-list">
            {#each selectedProject.skills as skill}
              <span class="skill-badge">{skill}</span>
            {/each}
          </div>
        </div>
      {/if}
    </div>
  </div>
  
  <div class="project-apply-section">
    {#if applyLoading}
      <div class="loading-spinner"></div>
    {:else if applicationError && !isAdminUser(user)}
      <div class="error-message">{applicationError}</div>
    {/if}

    {#if user && isAdminUser(user)}
      <div class="admin-applicants-section">
        <div class="admin-section-header">
          <h3 class="admin-section-title">지원자 현황</h3>
          <button class="refresh-btn" on:click={() => onFetchProjectApplications(selectedProject.id)}>
            새로고침
          </button>
        </div>
        
        <div class="applicants-summary">
          <div class="summary-item text-center">
            <span class="summary-label">지원자</span>
            <span class="summary-value">{getProjectApplications().length}명</span>
          </div>
          <div class="summary-item text-center">
            <span class="summary-label">검토중</span>
            <span class="summary-value">{getApplicationsByStatus('reviewing').length}명</span>
          </div>
          <div class="summary-item text-center">
            <span class="summary-label">합격</span>
            <span class="summary-value">{getApplicationsByStatus('accepted').length}명</span>
          </div>
        </div>
        
        <div class="admin-applicants-container">
          {#if getProjectApplications().length > 0}
            <div class="applications-table">
              <div class="application-row table-header">
                <div class="application-cell">지원자</div>
                <div class="application-cell">지원일</div>
                <div class="application-cell">면접가능일</div>
                <div class="application-cell">출근가능일</div>
                <div class="application-cell">단가</div>
                <div class="application-cell">상태</div>
              </div>
              
              {#each getProjectApplications() as app}
                <div class="application-row">
                  <div class="application-cell">
                    {getUserDisplayName(app)}
                  </div>
                  <div class="application-cell">
                    {getApplicationDate(app)}
                  </div>
                  <div class="application-cell">
                    {getAvailableDate(app)}
                  </div>
                  <div class="application-cell">
                    {getStartWorkDate(app)}
                  </div>
                  <div class="application-cell">
                    {app.price ? `${app.price}만원` : '-'}
                  </div>
                  <div class="application-cell">
                    {#if getStatusDisplay(app)}
                      <span class="status-badge {getStatusDisplay(app).class}">
                        {getStatusDisplay(app).label}
                      </span>
                    {/if}
                  </div>
                </div>
              {/each}
            </div>
          {:else}
            <div class="empty-applications">
              <p>아직 지원자가 없습니다.</p>
            </div>
          {/if}
        </div>
      </div>
    {:else if user}
      {#if !isApplyDisabled}
        <div class="apply-form">
          <div class="price-input-container">
            <label for="project-price">프로젝트 지원 단가</label>
            <div class="price-input-wrapper">
              <input 
                type="text"
                id="project-price"
                bind:value={projectPrice}
                on:input={validateNumber}
                placeholder="숫자만 입력해주세요"
                class="price-input"
              />
              <span class="price-unit">만원</span>
            </div>
          </div>
          
          <div class="available-date-container">
            <label for="available-date">면접가능일</label>
            <input 
              type="text"
              id="available-date"
              bind:value={availableDate}
              placeholder="면접 가능일을 입력해주세요 (예: 즉시 또는 날짜로 기재 등)"
              class="text-input"
            />
          </div>
          
          <div class="start-work-date-container">
            <label for="start-work-date">출근가능일</label>
            <input 
              type="text"
              id="start-work-date"
              bind:value={startWorkDate}
              placeholder="출근 가능일을 입력해주세요 (예: 즉시 또는 날짜로 기재 등)"
              class="text-input"
            />
          </div>
          
          <textarea 
            bind:value={applyMessage} 
            placeholder="해당 스킬 경험 관련 내용을 기재해 주세요."
            class="apply-message"
          ></textarea>
          
          <button class="apply-button" on:click={onApplyToProject} disabled={applyLoading}>
            프로젝트에 참여신청
          </button>
        </div>
      {:else}
        <div class="apply-disabled-message {!hasUserFiles ? 'file-upload-required' : ''}">{applyDisabledReason}</div>
      {/if}
    {:else}
      <div class="login-required">
        <div class="login-message">
          <h3>로그인이 필요합니다</h3>
          <p>프로젝트에 지원하려면 로그인해주세요.</p>
          <button class="login-btn" on:click={() => onSetPage('login')}> 
            로그인하기
          </button>
        </div>
      </div>
    {/if}
  </div>
  
  <button class="back-button" on:click={onCloseProjectDetail}>
    &lt; 목록보기
  </button>
</div>
{/if}

<style>
  /* ====================== 
     프로젝트 상세 페이지
     ====================== */
  .project-detail-container {
    max-width: 1150px;
    margin: 0 auto;
    padding: 20px;
  }

  .project-detail-header {
    background-color: white;
    border-radius: 8px;
    padding: 40px 30px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid #e9ecef;
  }

  .detail-badge-container {
    display: flex;
    gap: 8px;
    margin-bottom: 25px;
  }

  .detail-badge {
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 500;
    border: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 36px;
  }

  .detail-badge:first-child {
    background-color: #f8f9fa;
    color: #495057;
  }

  .detail-badge.outsourcing {
    background-color: #28a745;
    color: white;
  }

  .detail-badge.status.모집중 {
    background-color: #007bff;
    color: white;
  }

  .detail-badge.status.마감 {
    background-color: #6c757d;
    color: white;
  }

  .detail-badge.skill-badge {
    background-color: #f8f9fa;
    color: #495057;
  }

  .detail-badge.skill-badge.more {
    background-color: #e9ecef;
    color: #6c757d;
    font-weight: 600;
  }

  .project-detail-title {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 30px;
    line-height: 1.4;
    color: #333;
    word-break: keep-all;
  }

  .project-basic-info {
    display: flex;
    gap: 30px;
    margin-bottom: 25px;
    padding-bottom: 20px;
    border-bottom: 1px solid #e9ecef;
  }

  .basic-info-row {
    display: flex;
    align-items: center;
  }

  .basic-info-row .info-label {
    font-size: 0.9rem;
    color: #666;
    margin-right: 8px;
    min-width: 40px;
  }

  .basic-info-row .info-value {
    font-size: 0.95rem;
    color: #333;
    font-weight: 500;
  }

  .project-info-section {
    margin-bottom: 20px;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-bottom: 20px;
  }

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .info-item .info-label {
    font-size: 0.9rem;
    color: #666;
    font-weight: 500;
  }

  .info-item .info-value {
    font-size: 0.95rem;
    color: #333;
    font-weight: 500;
  }

  .detail-updated {
    font-size: 0.85rem;
    color: #868e96;
    text-align: right;
  }

  .project-detail-content {
    background-color: white;
    border-radius: 8px;
    padding: 30px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid #e9ecef;
  }

  .content-section-title {
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 20px;
    color: #333;
    padding-bottom: 10px;
    border-bottom: 1px solid #e9ecef;
  }

  .content-text {
    line-height: 1.6;
    color: #555;
  }

  .content-list {
    margin-bottom: 20px;
  }

  .content-item {
    margin-bottom: 12px;
    line-height: 1.5;
  }

  .content-number {
    color: #333;
    font-weight: 500;
    margin-right: 6px;
  }

  .content-label {
    color: #333;
    font-weight: 500;
    margin-right: 6px;
  }

  .content-value {
    color: #555;
  }

  .content-description {
    margin-top: 20px;
  }

  .content-description p {
    margin-bottom: 15px;
    line-height: 1.6;
  }

  .project-apply-section {
    background-color: white;
    border-radius: 8px;
    padding: 30px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid #e9ecef;
  }

  .apply-form {
    max-width: 600px;
    margin: 0 auto;
  }

  .price-input-container,
  .available-date-container,
  .start-work-date-container {
    margin-bottom: 20px;
  }

  .price-input-container label,
  .available-date-container label,
  .start-work-date-container label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #333;
  }

  .price-input-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .price-input,
  .text-input {
    flex: 1;
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
  }
  
  .text-input {
    width: 100%;
  }

  .price-unit {
    font-size: 0.95rem;
    color: #666;
    font-weight: 500;
  }

  /* 지원자 현황 개선된 스타일 */
  .admin-section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }

  .applicants-summary {
    display: flex;
    gap: 30px;
    margin-bottom: 25px;
    padding: 20px;
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
  }

  .applicants-summary .summary-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
  }

  .summary-label {
    font-size: 0.9rem;
    color: #666;
    font-weight: 500;
  }

  .summary-value {
    font-size: 1.2rem;
    color: #333;
    font-weight: 600;
  }

  /* 나이 표시 스타일 */
  .age-display {
    display: flex;
    align-items: center;
    padding: 10px 15px;
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 5px;
  }

  .age-text {
    font-size: 1rem;
    font-weight: 500;
    color: #3273dc;
  }

  /* 기술 스택 섹션 */
  .project-skills-section {
    margin-bottom: 25px;
  }

  .skills-title {
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 10px;
    color: #333;
  }

  .skills-badge-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .skill-badge {
    background-color: #f1f3f5;
    color: #495057;
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 500;
  }


  .summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 15px;
    margin-bottom: 15px;
  }


  .summary-label {
    font-size: 14px;
    color: #666;
    margin-bottom: 5px;
    font-weight: 500;
  }

  .summary-value {
    font-size: 16px;
    font-weight: 600;
    color: #333;
  }

  .summary-skills {
    margin-top: 15px;
    border-top: 1px dashed #ddd;
    padding-top: 15px;
  }

  .summary-skills .skills-badge-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
  }

  /* Admin applicants section */
  .admin-applicants-section {
    margin-bottom: 30px;
    width: 100%;
  }

  .admin-section-title {
    font-size: 1.3rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 15px;
    text-align: left;
    display: inline-block;
  }

  .refresh-btn {
    background-color: #f1f3f5;
    border: 1px solid #dee2e6;
    color: #495057;
    padding: 5px 10px;
    border-radius: 4px;
    font-size: 0.85rem;
    cursor: pointer;
    margin-left: 10px;
    vertical-align: middle;
  }

  .refresh-btn:hover {
    background-color: #e9ecef;
  }

  .admin-applicants-container {
    background-color: #f8f9fa;
    border-radius: 6px;
    padding: 15px;
    margin-bottom: 20px;
  }

  .section-divider {
    border: 0;
    height: 1px;
    background-color: #e9ecef;
    margin: 25px 0;
  }

  .applications-table {
    width: 100%;
    border-top: 1px solid #ddd;
    border-bottom: 1px solid #ddd;
  }

  .application-row {
    display: flex;
    border-bottom: 1px solid #eee;
  }

  .application-row:last-child {
    border-bottom: none;
  }

  .application-cell {
    flex: 1;
    padding: 15px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.95rem;
  }

  .table-header {
    background-color: #f8f9fa;
    font-weight: 600;
  }

  .empty-applications {
    text-align: center;
    padding: 50px 0;
    color: #868e96;
    background-color: #f8f9fa;
    border-radius: 4px;
    margin-top: 20px;
  }

  .apply-message {
    width: 100%;
    height: 100px;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    resize: vertical;
    font-family: inherit;
    font-size: 0.95rem;
  }

  .apply-button {
    background-color: #ff922b;
    color: white;
    border: none;
    padding: 12px 30px;
    font-size: 1.1rem;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    transition: background-color 0.2s;
    width: 100%;
  }

  .apply-button:hover {
    background-color: #e67700;
  }

  .back-button {
    display: inline-block;
    margin-top: 30px;
    background: none;
    border: none;
    color: #495057;
    font-size: 1rem;
    cursor: pointer;
    padding: 8px 0;
    font-weight: 500;
  }

  .back-button:hover {
    color: #343a40;
    text-decoration: underline;
  }

  /* 상태 뱃지 */
  .status-badge {
    display: inline-block;
    font-size: 0.75rem;
    font-weight: 500;
    padding: 3px 8px;
    border-radius: 12px;
    margin-left: 8px;
  }

  .status-badge.모집중 {
    background-color: #e7f5ff;
    color: #3273dc;
    border: 1px solid #3273dc;
  }

  .status-badge.마감 {
    background-color: #f1f3f5;
    color: #495057;
    border: 1px solid #adb5bd;
  }

  .status-badge.applied {
    background-color: #e7f5ff;
    color: #3273dc;
  }

  .status-badge.reviewing {
    background-color: #fff9db;
    color: #f59f00;
  }

  .status-badge.proceeding {
    background-color: #d3f9d8;
    color: #40c057;
  }

  .status-badge.accepted {
    background-color: #b2f2bb;
    color: #2b8a3e;
  }

  .status-badge.rejected {
    background-color: #ffe3e3;
    color: #e03131;
  }

  .status-badge.project_canceled,
  .status-badge.canceled {
    background-color: #ffe3e3;
    color: #fa5252;
  }

  .status-badge.project_delayed {
    background-color: #e5dbff;
    color: #7950f2;
  }

  .status-badge.closed {
    background-color: #f1f3f5;
    color: #868e96;
  }

  .loading-spinner {
    width: 50px;
    height: 50px;
    border: 5px solid #f3f3f3;
    border-top: 5px solid #3273dc;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 20px auto;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-message {
    color: #fa5252;
    margin-bottom: 15px;
    text-align: center;
  }

  .login-required {
    text-align: center;
    padding: 40px 20px;
  }

  .login-message h3 {
    margin-bottom: 10px;
    color: #333;
  }

  .login-message p {
    margin-bottom: 20px;
    color: #666;
  }

  .login-btn {
    background-color: #3273dc;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1rem;
  }

  .text-center {
    text-align: center;
  }

  .apply-disabled-message {
    text-align: center;
    padding: 15px;
    margin: 20px 0;
    color: #666;
    font-size: 1rem;
  }

  .apply-disabled-message.file-upload-required {
    background-color: #fff5f5;
    border: 1px solid #fed7d7;
    border-radius: 8px;
    color: #e53e3e;
    font-weight: 600;
    font-size: 1.1rem;
    padding: 20px;
    white-space: pre-line;
  }

  @media (max-width: 992px) {
    .info-grid {
      grid-template-columns: 1fr;
    }
    
    .project-basic-info {
      flex-direction: column;
      gap: 15px;
    }
  }

  @media (max-width: 576px) {
    .project-detail-title {
      font-size: 1.5rem;
    }
    
    .project-detail-header {
      padding: 20px;
    }
    
    .project-detail-content {
      padding: 20px;
    }
  }
</style>